import React, { useState, useEffect, useCallback } from 'react';
import { FaSpinner, FaTimes, FaPlus, FaPaperclip } from 'react-icons/fa';
import { ColoredDeliveryIcon, CloseIcon, PlusIcon } from 'ui-component/custom-icons/icons';

import Select from 'react-select';
import InputGroup from 'react-bootstrap/InputGroup';
import DropdownButton from 'react-bootstrap/DropdownButton';
import Dropdown from 'react-bootstrap/Dropdown';
import { useDropzone } from 'react-dropzone';
import toast from 'react-hot-toast';


// ✅ Clear
const PicklistCreateShipmentModalMobile = ({ openPopup, details, handleClose, handleReloadData }) => {

    const [disabledCreateShipmentButton, setDisabledCreateShipmentButton] = useState(false);
    const [shippingCompanies, setShippingCompanies] = useState([]);
    const [shipmentReferenceNo, setShipmentReferenceNo] = useState([]);
    const [defaultWeightUnit, setDefaultWeightUnit] = useState('');

    const [currencyList, setCurrencyList] = useState([]);
    const [attachmentCollections, setAttachmentCollections] = useState([]);
    const [optimizedSerials, setOptimizedSerials] = useState([]);
    const [additionalCharges, setAdditionalCharges] = useState([]);

    const [shipmentDetails, setShipmentDetails] = useState({
        picklist_id: details?.picklist_id,
        shipping_company_id: '',
        tracking_number: '',
        weight: '',
        weight_unit: 'kg',
        shipping_currency_id: '',
        shipping_cost: '',
        additional_charges: [],
    });

    const weightUnits = [
        { label: 'Kilogram', value: 'kg' },
        { label: 'Gram', value: 'g' },
    ];

    const formData = new FormData();

    useEffect(() => {
        
        const requests = details?.items?.map((item) => {
            return {
                id: item.id,
                collected_quantity: item.collected_quantity,
                picked_quantity: item.picked_quantity,
                quantity: item.quantity,
                show_scanner: item.show_scanner,
                product_id: item?.product?.id,
                serials: item?.serials?.map((s) => {
                    return {
                        id: s.id,
                        imei: s.imei,
                    }
                }),
            }
        });


        api.getSelectOptionsList('currency').then((res) => {
            const collection = res?.data?.map((item) => ({ id: item.id, label: item.currency, value: item.id }));
            setCurrencyList(collection);
            const defaultCurrency = res?.data?.find((item) => item.currency === 'USD ($)');
            setShipmentDetails({
                ...shipmentDetails,
                shipping_currency_id: defaultCurrency?.id
            })
		});

        api.getNextReferenceNumber('shipment').then((res) => {
            if (res?.status) {
                setShipmentReferenceNo(res?.data?.next_reference_no);
            }
        });
		api.getSelectOptionsList('shipping_company').then((res) => {
			// option_product_name: "11P256MIXAA+ - iPhone 11 Pro 256GB Mixed AA+ - 0 stock"
			const data = res.data.map((item) => ({
				...item,
				label: `${item.name}`,
				value: item.id,
			}));
			setShippingCompanies(data);
        });
        setDefaultWeightUnit('Kilogram');
        setShipmentDetails({
            ...shipmentDetails,
            picklist_id: details?.picklist_id,
            weight_unit: 'kg',
        })
        setOptimizedSerials(requests);
    }, []);

    const handleChangeWeight = (e) => { 
        
        const weightValue = parseFloat(e.target.value);

        if (!isNaN(weightValue)) {
            setShipmentDetails({
                ...shipmentDetails,
                weight: weightValue
            })
        } else {
            setShipmentDetails({
                ...shipmentDetails,
                weight: 0
            })
        }
    }


	const onDrop = useCallback((acceptedFiles) => {
        toast.error('Invalid file extension. Please select a JPEG, JPG, PNG, or PDF file.');
	}, []);

	const { getRootProps, getInputProps } = useDropzone({
		onDrop,
		accept: '.pdf, .jpeg, .jpg, .png',
		multiple: false
    });
    


    const allowedExtensions = ['pdf', 'jpeg', 'jpg', 'png'];
    const validateFileExtension = (fileName) => {
        const extension = fileName.split('.').pop().toLowerCase();
        return allowedExtensions.includes(extension);
    };

	const onPaymentDrop = useCallback((acceptedFiles) => {

        formData.append('picklist_id', shipmentDetails?.picklist_id);
        const attachments = acceptedFiles.map((file, index) => {
            let sizeStr = "";
            const sizeInKB = file.size / 1024;
            if (sizeInKB >= 1024) {
                const sizeInMB = sizeInKB / 1024;
                sizeStr = `${sizeInMB.toFixed(2)} MB`;
            } else {
                sizeStr = `${sizeInKB.toFixed(2)} KB`;
            }

            if (validateFileExtension(file?.name)) {

                formData.append('attachments[]', file);
                return {
                    id: '',
                    filename: file?.name,
                    size: sizeStr,
                    attachment: file,
                    allowed: true
                }

            } else {
                return {
                    id: '',
                    filename: file?.name,
                    size: sizeStr,
                    attachment: file,
                    allowed: false
                }
            }
        })
        

        try {
            toast.promise(
                api
                    .uploadShipmentAttachment(formData)
                    .then(
                        (res) => {
                            if (res.status) {
                                const datas = res.data.map(item => {
                                    return {
                                        id: item.id,
                                        name: item.filename,
                                    }
                                })
                                setAttachmentCollections([...attachmentCollections, ...datas]);
                                formData.append('attachments', null);
                            }

                            return res?.status ? res : null;
                        },
                        () => {}
                    ),
                {
                    loading: 'uploading file...',
                    success: (data) => `${data.message}`,
                    error: <b>Failed to upload file</b>
                }
            );
            
        } catch (error) {
            toast.error('Error uploading file');
        }

        return false;
	}, []);
	const { getRootProps: getPaymentRootProps, getInputProps: getPaymentInputProps } = useDropzone({ onDrop: onPaymentDrop });




    const submitShipmentForm = () => {
        let requests = shipmentDetails;
        requests.items = optimizedSerials;
        requests.attachment_ids = attachmentCollections
        requests.additional_charges = additionalCharges;


        if (!requests?.picklist_id) {
            toast.error('Picklist is required');
            return;
        } else if (!requests?.shipping_company_id) {
            toast.error('Shipping Company is required');
            return;
        } else if (!requests?.tracking_number) {
            toast.error('Airway bill is required');
            return;
        } else if (!requests?.shipping_currency_id) {
            toast.error('Currency is required');
            return;
        } else if (!requests.shipping_cost.toString().trim()) {
            toast.error('Shipping cost is required');
            return;
        } else if (!requests.weight.toString().trim()) {
            toast.error('Weight is required');
            return;
		}
		
        setDisabledCreateShipmentButton(true);
        toast.promise(
            api
            .createShipmentFromPicklist(shipmentDetails?.picklist_id, requests)
            .then(
                (res) => {
                        if (res.status) {
                            handleReloadData()
                        }
    
                        return res?.status ? res : null;
                    },
                    () => { }
                ),
            {
                loading: 'Creating shipment...',
                success: (data) => `${data.message}`,
                error: <b>Failed to add shipment</b>
            }
        );
    }



    // Instead of Modal, use a full-screen fixed div like SendEmailMobileModal
    if (!openPopup) return null;

    return (
		<div
			className="d-flex"
			style={{
				width: '100vw',
				height: '100dvh',
				position: 'fixed',
				left: 0,
				top: 0,
				zIndex: 20,
				background: 'rgba(0,0,0,0.07)',
				justifyContent: 'center',
				alignItems: 'end',
				paddingBottom: '90px',
				backdropFilter: 'blur(8px)',
				WebkitBackdropFilter: 'blur(8px)'
			}}
		>
			<div
				className="popup product-popup-mobile d-md-none"
				style={{
					position: 'relative',
					transform: 'none',
					top: 0,
					left: 0,
					maxWidth: '90%',
					background: '#fff',
					borderRadius: 12,
					boxShadow: 'rgb(0 0 0 / 37%) 0px 0px 20px',
					width: '100%',
					minHeight: '70dvh',
					maxHeight: '80dvh',
					padding: '24px 16px 0 16px',
					display: 'flex',
					flexDirection: 'column'
				}}
			>
				<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
					<h5 style={{ fontWeight: 600, fontSize: 18, margin: 0 }}>Create Shipment</h5>
				</div>
				<div style={{ flex: 'auto', overflowY: 'auto', marginBottom: 12 }}>
					<div className="row m-0">
						<div className="col-12 text-center">
							<ColoredDeliveryIcon height="60px" width="60px" />
						</div>
					</div>
					<div className="row m-0 align-items-top">
						<div className="col-12 text-center " style={{ padding: '10px 0 20px 0' }}>
							<div style={{ fontSize: '24px' }}>Create Shipment</div>
							<div className="textGray">
								Manage shipment details for accurate <br />
								tracking and smooth delivery.
							</div>
						</div>
					</div>
					<div className="row m-0 align-items-top">
						<div className="col-12 text-left " style={{ padding: '5px 0' }}>
							<label>Reference No.</label>
							<input type="text" className="form-control" value={shipmentReferenceNo} disabled={true} />
						</div>
					</div>
					<div className="row m-0 align-items-top">
						<div className="col-12 col-md-6 text-left " style={{ padding: '5px 0' }}>
							<label>Shipping Provider</label>
							<Select
								options={shippingCompanies}
								className="rs-container"
								classNamePrefix="rs"
								placeholder="Select or type..."
								isMulti={false}
								onChange={(e) => {
									setShipmentDetails({
										...shipmentDetails,
										shipping_company_id: e.value
									});
								}}
							/>
						</div>
						<div className="col-12 col-md-6 text-left " style={{ padding: '5px 0 5px 5px' }}>
							<label>Airway bill code</label>
							<input
								type="text"
								className="form-control"
								value={shipmentDetails?.tracking_number}
								placeholder="Enter airway bill code"
								onChange={(e) => {
									setShipmentDetails({
										...shipmentDetails,
										tracking_number: e.target.value
									});
								}}
								style={{
									minHeight: '35px'
								}}
							/>
						</div>
					</div>
					<div className="row m-0 align-items-top">
						<div className="col-12 col-md-6 text-left " style={{ padding: '5px 0 0 0' }}>
							<label>Shipping cost</label>
							<div className="row m-0">
								<div className="col-12 d-flex input-group-2-form input-group-2-form-currency">
									<Select
										options={currencyList}
										className="rs-container left-input"
										classNamePrefix="rs"
										placeholder="Select"
										isMulti={false}
										onChange={(e) => {
											setShipmentDetails({
												...shipmentDetails,
												shipping_currency_id: e.value
											});
										}}
										value={currencyList?.find((option) => option.id === shipmentDetails?.shipping_currency_id) || null}
									/>
									<input
										type="number"
										className="form-control h36 right-input ml6"
										value={shipmentDetails?.shipping_cost}
										placeholder="0.00"
										onChange={(e) => {
											setShipmentDetails({
												...shipmentDetails,
												shipping_cost: e.target.value
											});
										}}
									/>
								</div>
							</div>
						</div>
						<div className="col-12 col-md-6 text-left " style={{ padding: '5px 0 5px 5px' }}>
							<label>Weight</label>
							<div className="custom-input-group">
								<InputGroup className="d-flex">
									<DropdownButton variant="outline-secondary" title={defaultWeightUnit} id="input-group-dropdown-1">
										{weightUnits.map((unit) => (
											<Dropdown.Item
												href="#"
												key={unit.value}
												onClick={() => {
													setShipmentDetails({
														...shipmentDetails,
														weight_unit: unit.value
													});
													setDefaultWeightUnit(unit.label);
												}}
											>
												{unit.label}
											</Dropdown.Item>
										))}
									</DropdownButton>
									<input
										type="number"
										name="input-name"
										className="form-control"
										placeholder="0"
										onChange={handleChangeWeight}
										value={shipmentDetails?.weight}
										autoComplete={'off'}
										autoFocus
									/>
								</InputGroup>
							</div>
						</div>
					</div>
					<div className="row m-0 align-items-top">
						<div className="col-6 text-left " style={{ padding: '10px 0 0 0' }}>
							<label style={{ marginTop: '7px' }}>Charges</label>
						</div>
						<div className="col-6 text-right d-flex justify-content-end" style={{ padding: '10px 0 0 0' }}>
							<button
								className="btn btn-main-primary btn-outlined-main-primary"
								style={{ minWidth: '0px' }}
								onClick={() => {
									setAdditionalCharges([
										...additionalCharges,
										{
											currency_id: shipmentDetails?.shipping_currency_id,
											amount: '',
											description: ''
										}
									]);
								}}
							>
								<PlusIcon height={'16px'} width={'16px'} />
							</button>
						</div>
					</div>
					{additionalCharges?.length > 0 && (
						<>
							{additionalCharges?.map((charge, index) => (
								<React.Fragment key={index}>
									<hr />
									<div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 12 }}>
										<div style={{ flex: 1 }}>
											<div style={{ marginBottom: 8 }}>
												<label>Amount</label>
												<div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
													<Select
														options={currencyList}
														className="rs-container left-input"
														classNamePrefix="rs"
														placeholder="Select"
														isMulti={false}
														value={currencyList?.find((option) => option.id === charge?.currency_id) || null}
														onChange={(e) => {
															const updatedCharges = [...additionalCharges];
															updatedCharges[index].currency_id = e.value;
															setAdditionalCharges(updatedCharges);
														}}
													/>
													<input
														type="number"
														className="form-control h36 right-input ml6"
														value={charge?.amount}
														placeholder="0.00"
														onChange={(e) => {
															const updatedCharges = [...additionalCharges];
															updatedCharges[index].amount = e.target.value;
															setAdditionalCharges(updatedCharges);
														}}
													/>
												</div>
											</div>
											<div>
												<label>Description</label>
												<textarea
													onChange={(e) => {
														const updatedCharges = [...additionalCharges];
														updatedCharges[index].description = e.target.value;
														setAdditionalCharges(updatedCharges);
													}}
													value={charge?.description}
													placeholder="Enter description"
													style={{
														width: '100%',
														height: 35,
														padding: '4px 8px',
														border: '1px solid #ced4da',
														borderRadius: 4,
														resize: 'none'
													}}
												></textarea>
											</div>
										</div>
										<div style={{ display: 'flex', alignItems: 'center', marginLeft: 8, marginTop: 24 }}>
											<button
												className="btn modal-close-icon"
												onClick={() => {
													const updatedCharges = additionalCharges.filter((_, i) => i !== index);
													setAdditionalCharges(updatedCharges);
												}}
												style={{
													position: 'relative',
													marginLeft: 7,
													background: 'transparent',
													border: 'none',
													padding: 4,
													cursor: 'pointer'
												}}
											>
												<CloseIcon height={'10px'} width={'10px'} />
											</button>
										</div>
									</div>
								</React.Fragment>
							))}
						</>
					)}
					<div className="row m-0 align-items-top">
						<div className="col-12 text-left " style={{ padding: '10px 0' }}>
							<div {...getPaymentRootProps()}>
								<div className="add-item-empty-div">
									<button type="button" className="btn btn-primary btn-outlined-main-primary dragText">
										<FaPlus />
										Add file here
									</button>
								</div>
								<input {...getPaymentInputProps({ disabled: false })} style={{ display: 'none' }} />{' '}
							</div>
						</div>
					</div>
					{attachmentCollections?.length > 0 && (
						<div className="row m-0 align-items-top">
							<div className="col-12 text-left " style={{ padding: '10px 0' }}>
								{attachmentCollections?.map((attachment, index) => (
									<div className="payment-modal-attachment-container" key={index}>
										<div className="row m-0">
											<div className="col-12">
												<div className="row m-0">
													<div className="col-12" style={{ display: 'flex', alignItems: 'center' }}>
														<div className="payment-modal-attachment-icon" style={{ padding: '5px 0px', border: '0' }}>
															<FaPaperclip />
														</div>
														<div className="payment-modal-attachment-name">
															<div>{attachment?.name}</div>
														</div>
														<div className="payment-modal-attachment-delete">
															<svg
																id="Component_127_1"
																height="12"
																width="12"
																data-name="Component 127 – 1"
																xmlns="http://www.w3.org/2000/svg"
																viewBox="0 0 19.295 19.289"
															>
																<path
																	id="Icon_ionic-ios-close"
																	data-name="Icon ionic-ios-close"
																	d="M23.219,20.933l6.891-6.891a1.615,1.615,0,0,0-2.284-2.284L20.935,18.65l-6.891-6.891a1.615,1.615,0,1,0-2.284,2.284l6.891,6.891L11.76,27.825a1.615,1.615,0,1,0,2.284,2.284l6.891-6.891,6.891,6.891a1.615,1.615,0,1,0,2.284-2.284Z"
																	transform="translate(-11.285 -11.289)"
																/>
															</svg>
														</div>
													</div>
												</div>
											</div>
										</div>
									</div>
								))}
							</div>
						</div>
					)}
				</div>
				{/* Sticky bottom buttons */}
				<div
					className="btnarea"
					style={{
						position: 'sticky',
						bottom: 0,
						left: 0,
						width: '100%',
						background: '#fff',
						padding: '16px 0 12px 0',
						zIndex: 30,
						display: 'flex',
						gap: 10,
						justifyContent: 'center',
						marginTop: 'auto'
					}}
				>
					<button
						className="btn btn-main-primary btn-outlined-main-primary btn-block"
						style={{ marginRight: '12px' }}
						disabled={disabledCreateShipmentButton}
						onClick={() => {
							if (disabledCreateShipmentButton) {
								toast.error('Please wait, processing shipment creation...');
								return;
							} else {
								submitShipmentForm();
							}
						}}
					>
						{disabledCreateShipmentButton ? (
							<>
								<FaSpinner className="fa-spin" /> Creating shipment. Please wait...
							</>
						) : (
							'Create Shipment'
						)}
					</button>
					<button className="btn new-btn-error" onClick={handleClose}>
						<FaTimes /> Close
					</button>
				</div>
			</div>
		</div>
	);
};

export default PicklistCreateShipmentModalMobile;
